// src/pages/components/AssigneeSelector.tsx
import React, { useState } from 'react';
import { tasksApi, TaskAssignee, AssigneeIn } from '../api/tasks';
import { useUserInfo } from '../hooks/useUserInfo';
import PersonAvatar from './people/PersonAvatar';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { Popup } from '../components/Popup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PreparorSelect } from '../pages/Task/PreparorSelect';
import ConfirmDialog, { ConfirmState } from './ConfirmDialog';

interface AssigneeSelectorProps {
  taskId: string;
  assignees: TaskAssignee[];
  onRefresh: () => void;
}

function AssigneeSelector({ taskId, assignees, onRefresh }: AssigneeSelectorProps) {
  const { guard, showError, showSuccess } = usePermissionGuard();
  const [showAdd, setShowAdd] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newRole, setNewRole] = useState<'OWNER' | 'COLLABORATOR' | 'PREPAROR' | 'REVIEWER'>('COLLABORATOR');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const { currentUser, getUserName, getUserRole, getAllUsersList, loading } = useUserInfo();

  const allUsers = getAllUsersList();

  const handleAdd = async () => {
    if (!newUserId.trim()) {
      showError('CANNOT_ASSIGN_TO_SELF', 'Please select a user to assign');
      return;
    }

    if (newUserId === currentUser?.id) {
      showError('CANNOT_ASSIGN_TO_SELF', 'You cannot assign a task to yourself. Tasks are automatically assigned to you when created.');
      return;
    }

    setAdding(true);
    await guard(async () => {
      const newAssignee: AssigneeIn = {
        user_id: newUserId,
        role_in_task: newRole,
      };
      await tasksApi.assignUsers(taskId, { assignees: [newAssignee], replace: false });
      setNewUserId('');
      setShowAdd(false);
      const assignedUser = allUsers.find(u => u.id === newUserId);
      showSuccess('Assignee Added', `${assignedUser?.name || 'User'} has been assigned as ${newRole}`);
      onRefresh();
    }, 'CANNOT_ASSIGN_TO_HIGHER_LEVEL');
    
    setAdding(false);
  };

  const handleRemove = async (userId: string) => {
    const userName = getUserName(userId);
    setConfirm({
      title: `Remove ${userName} from this task?`,
      confirmLabel: 'Remove',
      tone: 'danger',
      onConfirm: async () => {
        setRemoving(userId);
        try {
          await tasksApi.removeAssignee(taskId, userId);
          showSuccess('Assignee Removed', `${userName} has been removed from this task`);
          onRefresh();
        } catch (err: any) {
          showError('CANNOT_MODIFY_OTHERS_TASK', err.message || 'Failed to remove assignee');
        } finally {
          setRemoving(null);
        }
      },
    });
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'OWNER': return 'bg-gradient-to-r from-purple-500 to-purple-600 text-white';
      case 'COLLABORATOR': return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
      case 'PREPAROR': return 'bg-gradient-to-r from-green-500 to-green-600 text-white';
      case 'REVIEWER': return 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'OWNER': return '👑';
      case 'COLLABORATOR': return '🤝';
      case 'PREPAROR': return '📝';
      case 'REVIEWER': return '🔍';
      default: return '👤';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-5 mb-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-500">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-5 mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Assignees</h3>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{assignees.length}</span>
        </div>
        <button 
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium hover:bg-blue-50 rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Assignee
        </button>
      </div>

      {showAdd && (
        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px]">
              {/* Searchable user picker (no lens) — excludes the current user
                  and anyone already assigned. */}
              <PreparorSelect
                value={newUserId}
                onChange={(v) => setNewUserId(v)}
                options={allUsers
                  .filter(user => user.id !== currentUser?.id)
                  .filter(user => !assignees.some(a => a.user_id === user.id))
                  .map(user => ({ id: user.id, name: user.name, role: user.role }))}
                placeholder="Select user..."
              />
            </div>
            <div className="min-w-[180px]">
              <Select
                value={newRole}
                onValueChange={(v) => setNewRole(v as any)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COLLABORATOR">🤝 Collaborator</SelectItem>
                  <SelectItem value="OWNER">👑 Owner</SelectItem>
                  <SelectItem value="PREPAROR">📝 Preparator</SelectItem>
                  <SelectItem value="REVIEWER">🔍 Reviewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <button 
              onClick={handleAdd} 
              disabled={adding || !newUserId}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50"
            >
              {adding ? 'Adding...' : 'Add'}
            </button>
            <button 
              onClick={() => setShowAdd(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
          {allUsers.length === 0 && (
            <p className="text-xs text-amber-600 mt-2">
              ⚠️ No users found. Please check if users are loaded correctly.
            </p>
          )}
          {allUsers.filter(u => u.id !== currentUser?.id).length === 0 && (
            <p className="text-xs text-amber-600 mt-2">
              ⚠️ No other users available to assign. Only you are in the system.
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        {assignees.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p className="text-sm">No assignees yet</p>
            <p className="text-xs">Click "Add Assignee" to assign this task</p>
          </div>
        ) : (
          assignees.map((assignee) => {
            const userName = assignee.employee_name || getUserName(assignee.user_id);
            const userRole = getUserRole(assignee.user_id);
            const isCurrentUser = currentUser?.id === assignee.user_id;
            const isRemoving = removing === assignee.user_id;

            return (
              <div key={assignee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group">
                <div className="flex items-center gap-3 flex-1">
                  {/* Profile photo, with the role emoji kept as a small corner badge. */}
                  <div className="relative shrink-0 w-10 h-10">
                    <PersonAvatar userId={assignee.user_id} name={userName} size={40} />
                    <span
                      aria-hidden="true"
                      title={assignee.role_in_task}
                      className="absolute -bottom-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-gray-200 shadow-sm text-[11px] leading-none"
                    >
                      {getRoleIcon(assignee.role_in_task)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">
                        {userName}
                        {isCurrentUser && <span className="ml-1 text-xs text-blue-600">(You)</span>}
                      </span>
                      {/* <span className="text-xs text-gray-500">({assignee.user_id.slice(0, 8)}...)</span> */}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(assignee.role_in_task)}`}>
                        {assignee.role_in_task}
                      </span>
                      {assignee.progress_percent > 0 && (
                        <span className="text-xs text-gray-500">• {assignee.progress_percent}% complete</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Role: {userRole} • Assigned: {new Date(assignee.assigned_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                {!isCurrentUser && (
                  <button 
                    onClick={() => handleRemove(assignee.user_id)}
                    disabled={isRemoving}
                    className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 transition-all disabled:opacity-50"
                  >
                    {isRemoving ? (
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Summary Section */}
      {assignees.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Total Assignees: {assignees.length}</span>
            <span>Owner: {(() => { const o = assignees.find(a => a.role_in_task === 'OWNER'); return o ? (o.employee_name || getUserName(o.user_id)) : 'None'; })()}</span>
          </div>
        </div>
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

export default AssigneeSelector;